// src/pages/Institutional/TermsOfService.js
import React from 'react'
import { Box, Typography, Paper, Divider, Chip } from '@mui/material'
import Secao, { Leitura } from '../../components/Layout/Secao'
import { Gavel, Security, Block, Warning, Info } from '@mui/icons-material'

const TermsOfService = () => {
  const currentDate = new Date().toLocaleDateString('pt-BR', {
    month: 'long',
    year: 'numeric'
  })

  const accountTypes = [
    'Proprietário',
    'Corretor (com CRECI)',
    'Imobiliária (com CNPJ)'
  ]

  const prohibitedContent = [
    'Informações falsas ou enganosas',
    'Fotos de terceiros sem autorização',
    'Conteúdo ofensivo ou ilegal',
    'Anúncios duplicados',
    'Materiais publicitários exagerados',
    'Montagens enganosas'
  ]

  const notResponsibleFor = [
    'Negociações entre usuários',
    'Visitas presenciais',
    'Contratos firmados entre as partes',
    'Problemas estruturais ou legais dos imóveis',
    'Prejuízos financeiros',
    'Informações falsas fornecidas por anunciantes'
  ]

  return (
    // Mesmo trilho do cabecalho e do rodape; a largura de leitura de
    // 768px fica dentro dele, encostada na esquerda. Era Container md
    // centralizado: o texto comecava 256px a direita do logo acima dele.
    <Secao fundo='padrao' ritmo='solto' sx={{ minHeight: '100vh' }}>
      <Leitura>
        {/* Header */}
        <Box sx={{ mb: 6 }}>
          <Gavel sx={{ fontSize: '4rem', color: 'primary.main', mb: 2 }} />
          <Typography
            variant='h3'
            sx={{ fontWeight: 700, color: 'text.primary', mb: 2 }}
          >
            Termos de Uso
          </Typography>
          <Chip
            label={`Última atualização: ${currentDate}`}
            sx={{ fontWeight: 500 }}
          />
        </Box>

        {/* Introdução */}
        <Paper
          elevation={0}
          sx={{ p: 4, mb: 4, bgcolor: 'info.light', borderRadius: 3 }}
        >
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <Info sx={{ color: 'info.main', mt: 0.5 }} />
            <Box>
              <Typography
                variant='body1'
                sx={{ lineHeight: 1.8, color: 'text.primary' }}
              >
                Bem-vindo ao <strong>umarkt</strong>. Ao acessar, navegar ou
                utilizar qualquer funcionalidade da plataforma, você concorda
                com estes Termos de Uso.
              </Typography>
              <Typography
                variant='body1'
                sx={{ lineHeight: 1.8, color: 'text.primary', mt: 1 }}
              >
                Caso não concorde, recomendamos que não utilize o serviço.
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* 1. Sobre a umarkt */}
        <Paper
          elevation={0}
          sx={{ p: 4, mb: 3, bgcolor: 'white', borderRadius: 3 }}
        >
          <Typography
            variant='h5'
            sx={{ fontWeight: 700, mb: 2, color: 'text.primary' }}
          >
            1. Sobre a umarkt
          </Typography>
          <Typography
            variant='body1'
            sx={{ lineHeight: 1.8, color: 'text.secondary' }}
          >
            A umarkt é um marketplace digital que permite a publicação e a
            visualização de anúncios de imóveis.
          </Typography>
          <Box
            sx={{
              mt: 2,
              p: 2,
              bgcolor: 'warning.light',
              borderRadius: 2,
              borderLeft: '4px solid',
              borderColor: 'warning.main'
            }}
          >
            <Typography
              variant='body2'
              sx={{ fontWeight: 600, color: 'text.primary' }}
            >
              Importante: Não atuamos como imobiliária, corretora ou
              intermediadora de transações. Toda negociação ocorre diretamente
              entre anunciante e interessado.
            </Typography>
          </Box>
        </Paper>

        {/* 2. Cadastro e Responsabilidades */}
        <Paper
          elevation={0}
          sx={{ p: 4, mb: 3, bgcolor: 'white', borderRadius: 3 }}
        >
          <Typography
            variant='h5'
            sx={{ fontWeight: 700, mb: 3, color: 'text.primary' }}
          >
            2. Cadastro e Responsabilidades do Usuário
          </Typography>

          <Typography
            variant='h6'
            sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}
          >
            2.1. Informações verdadeiras
          </Typography>
          <Typography
            variant='body1'
            sx={{ lineHeight: 1.8, color: 'text.secondary', mb: 3 }}
          >
            Ao criar uma conta, o usuário se compromete a fornecer dados reais e
            atualizados.
          </Typography>

          <Typography
            variant='h6'
            sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}
          >
            2.2. Tipos de conta
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
            {accountTypes.map((type, index) => (
              <Chip key={index} label={type} variant='outlined' />
            ))}
          </Box>

          <Typography
            variant='h6'
            sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}
          >
            2.3. Segurança do acesso
          </Typography>
          <Typography
            variant='body1'
            sx={{ lineHeight: 1.8, color: 'text.secondary' }}
          >
            O usuário é responsável por manter sua senha em segurança e por
            todas as ações realizadas em sua conta.
          </Typography>
        </Paper>

        {/* 3. Publicação de Imóveis */}
        <Paper
          elevation={0}
          sx={{ p: 4, mb: 3, bgcolor: 'white', borderRadius: 3 }}
        >
          <Typography
            variant='h5'
            sx={{ fontWeight: 700, mb: 3, color: 'text.primary' }}
          >
            3. Publicação de Imóveis
          </Typography>

          <Typography
            variant='h6'
            sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}
          >
            3.1. Responsabilidade do anunciante
          </Typography>
          <Typography
            variant='body1'
            sx={{ lineHeight: 1.8, color: 'text.secondary', mb: 2 }}
          >
            O anunciante é inteiramente responsável:
          </Typography>
          <Box component='ul' sx={{ pl: 3, mb: 3 }}>
            <Typography
              component='li'
              variant='body2'
              sx={{ color: 'text.secondary', mb: 0.5 }}
            >
              Pelas informações publicadas
            </Typography>
            <Typography
              component='li'
              variant='body2'
              sx={{ color: 'text.secondary', mb: 0.5 }}
            >
              Pelas fotos enviadas
            </Typography>
            <Typography
              component='li'
              variant='body2'
              sx={{ color: 'text.secondary', mb: 0.5 }}
            >
              Pela veracidade dos dados
            </Typography>
            <Typography
              component='li'
              variant='body2'
              sx={{ color: 'text.secondary', mb: 0.5 }}
            >
              Pelo preço e condições do imóvel
            </Typography>
            <Typography
              component='li'
              variant='body2'
              sx={{ color: 'text.secondary', mb: 0.5 }}
            >
              Pela atualização ou remoção do anúncio
            </Typography>
          </Box>

          <Typography
            variant='h6'
            sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}
          >
            3.2. Conteúdos proibidos
          </Typography>
          <Typography
            variant='body1'
            sx={{ lineHeight: 1.8, color: 'text.secondary', mb: 2 }}
          >
            Não é permitido publicar:
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
            {prohibitedContent.map((item, index) => (
              <Box
                key={index}
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <Block sx={{ color: 'error.main', fontSize: '1.2rem' }} />
                <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                  {item}
                </Typography>
              </Box>
            ))}
          </Box>

          <Typography
            variant='h6'
            sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}
          >
            3.3. Remoção de anúncios
          </Typography>
          <Box
            sx={{
              p: 2,
              bgcolor: 'error.light',
              borderRadius: 2,
              borderLeft: '4px solid',
              borderColor: 'error.main'
            }}
          >
            <Typography variant='body2' sx={{ color: 'text.primary' }}>
              A umarkt pode remover anúncios que violem estes termos ou
              prejudiquem a experiência dos usuários.
            </Typography>
          </Box>
        </Paper>

        {/* 4. Funcionamento da Plataforma */}
        <Paper
          elevation={0}
          sx={{ p: 4, mb: 3, bgcolor: 'white', borderRadius: 3 }}
        >
          <Typography
            variant='h5'
            sx={{ fontWeight: 700, mb: 3, color: 'text.primary' }}
          >
            4. Funcionamento da Plataforma
          </Typography>

          <Typography
            variant='h6'
            sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}
          >
            4.1. Disponibilidade
          </Typography>
          <Typography
            variant='body1'
            sx={{ lineHeight: 1.8, color: 'text.secondary', mb: 3 }}
          >
            O portal pode passar por manutenções programadas ou instabilidades.
            Esforços são feitos para manter o serviço estável, mas a
            disponibilidade não é garantida.
          </Typography>

          <Typography
            variant='h6'
            sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}
          >
            4.2. Contatos
          </Typography>
          <Typography
            variant='body1'
            sx={{ lineHeight: 1.8, color: 'text.secondary', mb: 1 }}
          >
            Os interessados entram em contato com os anunciantes via:
          </Typography>
          <Box component='ul' sx={{ pl: 3, mb: 3 }}>
            <Typography
              component='li'
              variant='body2'
              sx={{ color: 'text.secondary', mb: 0.5 }}
            >
              WhatsApp
            </Typography>
            <Typography
              component='li'
              variant='body2'
              sx={{ color: 'text.secondary', mb: 0.5 }}
            >
              Formulário do portal
            </Typography>
            <Typography
              component='li'
              variant='body2'
              sx={{ color: 'text.secondary', mb: 0.5 }}
            >
              E-mail (opcional)
            </Typography>
          </Box>
          <Typography
            variant='body2'
            sx={{ fontWeight: 600, color: 'text.primary' }}
          >
            A umarkt não participa das negociações.
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Typography
            variant='h6'
            sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}
          >
            4.3. Ranking e destaque
          </Typography>
          <Typography
            variant='body1'
            sx={{ lineHeight: 1.8, color: 'text.secondary' }}
          >
            A ordem dos anúncios é determinada por critérios de relevância,
            qualidade e engajamento, podendo incluir destaque pago.
          </Typography>
        </Paper>

        {/* 5. Privacidade e Dados */}
        <Paper
          elevation={0}
          sx={{ p: 4, mb: 3, bgcolor: 'white', borderRadius: 3 }}
        >
          <Typography
            variant='h5'
            sx={{ fontWeight: 700, mb: 2, color: 'text.primary' }}
          >
            5. Privacidade e Dados
          </Typography>
          <Typography
            variant='body1'
            sx={{ lineHeight: 1.8, color: 'text.secondary' }}
          >
            As informações fornecidas pelos usuários são tratadas de acordo com
            nossa <strong>Política de Privacidade</strong>, disponível no site.
            Ao usar a plataforma, você concorda com o processamento de dados
            conforme descrito nela.
          </Typography>
        </Paper>

        {/* 6. Limitação de Responsabilidade */}
        <Paper
          elevation={0}
          sx={{ p: 4, mb: 3, bgcolor: 'white', borderRadius: 3 }}
        >
          <Typography
            variant='h5'
            sx={{ fontWeight: 700, mb: 3, color: 'text.primary' }}
          >
            6. Limitação de Responsabilidade
          </Typography>
          <Box
            sx={{
              p: 3,
              bgcolor: 'warning.light',
              borderRadius: 2,
              mb: 2
            }}
          >
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
              <Warning sx={{ color: 'warning.dark', mt: 0.5 }} />
              <Typography
                variant='body1'
                sx={{ fontWeight: 600, color: 'text.primary' }}
              >
                A umarkt não é responsável por:
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {notResponsibleFor.map((item, index) => (
              <Box
                key={index}
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: 'warning.main'
                  }}
                />
                <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                  {item}
                </Typography>
              </Box>
            ))}
          </Box>
          <Box
            sx={{
              mt: 3,
              p: 2,
              bgcolor: 'grey.100',
              borderRadius: 2
            }}
          >
            <Typography
              variant='body2'
              sx={{ fontWeight: 600, color: 'text.primary' }}
            >
              A plataforma é apenas um ambiente de divulgação.
            </Typography>
          </Box>
        </Paper>

        {/* 7. Penalidades e Suspensão */}
        <Paper
          elevation={0}
          sx={{ p: 4, mb: 3, bgcolor: 'white', borderRadius: 3 }}
        >
          <Typography
            variant='h5'
            sx={{ fontWeight: 700, mb: 2, color: 'text.primary' }}
          >
            7. Penalidades e Suspensão
          </Typography>
          <Typography
            variant='body1'
            sx={{ lineHeight: 1.8, color: 'text.secondary', mb: 2 }}
          >
            A conta do usuário pode ser:
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Chip label='Suspensa' color='error' size='small' />
            <Chip label='Limitada' color='warning' size='small' />
            <Chip label='Removida' color='error' size='small' />
          </Box>
          <Typography
            variant='body1'
            sx={{ lineHeight: 1.8, color: 'text.secondary' }}
          >
            Caso viole estes Termos, cause danos a terceiros ou prejudique o
            funcionamento do marketplace.
          </Typography>
        </Paper>

        {/* 8. Atualizações dos Termos */}
        <Paper
          elevation={0}
          sx={{ p: 4, mb: 3, bgcolor: 'white', borderRadius: 3 }}
        >
          <Typography
            variant='h5'
            sx={{ fontWeight: 700, mb: 2, color: 'text.primary' }}
          >
            8. Atualizações dos Termos
          </Typography>
          <Typography
            variant='body1'
            sx={{ lineHeight: 1.8, color: 'text.secondary' }}
          >
            Os Termos de Uso podem ser atualizados periodicamente. A versão mais
            recente estará sempre disponível no site.
          </Typography>
        </Paper>

        {/* 9. Contato */}
        <Paper
          elevation={0}
          sx={{
            p: 4,
            bgcolor: 'primary.main',
            color: 'white',
            borderRadius: 3,
            textAlign: 'center'
          }}
        >
          <Security sx={{ fontSize: '3rem', mb: 2 }} />
          <Typography variant='h5' sx={{ fontWeight: 700, mb: 2 }}>
            9. Contato
          </Typography>
          <Typography variant='body1' sx={{ mb: 2, opacity: 0.9 }}>
            Em caso de dúvidas sobre estes Termos:
          </Typography>
          <Typography variant='body2' sx={{ mb: 0.5 }}>
            <strong>E-mail:</strong> contato@umarkt.com.br
          </Typography>
          <Typography variant='body2' sx={{ mb: 0.5 }}>
            <strong>WhatsApp:</strong> (11) 9 8139-6930
          </Typography>
          <Typography variant='body2'>
            <strong>Site:</strong> www.umarkt.com.br
          </Typography>
        </Paper>
      </Leitura>
    </Secao>
  )
}

export default TermsOfService
